// Per-boat Settings. Three things live here now:
//
//   * what kind of boat this is — Power or Sail (ADR 0173). It belongs on a
//     settings page because it is a fact about the boat that outlives any one
//     screen, even though the surface it visibly changes is Alarms (the Alarms
//     page asks the question once, of a boat nobody has answered for; THIS is
//     where it is changed afterwards);
//   * each battery bank's chemistry — lithium or lead-acid, or automatic
//     (TOM-318, core ADR-197), which decides the battery alarm thresholds;
//   * removing the boat from your account (ADR 0165);
//   * the boat's owners, inviting and removing them, and the units on the
//     boat's own displays when it has more than one owner (TOM-458, ADR 0319).
//     With other owners, Delete becomes Leave: only the last owner deletes;
//   * the boat's own photo for the top of its home (TOM-683, ADR 0397), which
//     every owner sees;
//   * the boat's cameras' UniFi Protect console and API key, and its
//     certificate (TOM-835, ADR 0412), which every owner may set;
//   * the screen look — System, Light, Dark or Night (TOM-159). It belongs to
//     the owner's account (Night to this device, TOM-944), not the boat, but
//     Settings is where owners look for it.
//
// It exists as its own page because most owners have a single boat, and
// /dashboard redirects a single-boat account straight into that boat — so the
// fleet list, where a delete control would otherwise live, is a screen those
// users never see. The boat-scoped nav is the only place they can reliably
// find it.

import { unitLabel } from '@neomaris/protocol';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth, useDemoGuest } from '../auth';
import { useBoatChrome } from '../components/app/boatChrome';
import { DeleteBoatConfirm } from '../components/app/DeleteBoatConfirm';
import { LeaveBoatConfirm } from '../components/app/LeaveBoatConfirm';
import { BatteryChemistryChoice } from '../components/boat/BatteryChemistryChoice';
import { BoatPhotoPanel } from '../components/boat/BoatPhotoPanel';
import { BoatOwnersPanel } from '../components/boat/BoatOwnersPanel';
import { OperatorTokenPanel } from '../components/boat/OperatorTokenPanel';
import { ProtectPanel } from '../components/boat/ProtectPanel';
import { VesselTypeChoice } from '../components/boat/VesselTypeChoice';
import { ScreenLookChoice } from '../components/ScreenLookChoice';
import { ownersErrorMessage, sendDisplayUnits, useBoatOwners } from '../lib/boatOwners';
import { getBoatPresence } from '../lib/boatPresence';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useUnits } from '../lib/useUnits';
import { VESSEL_TYPE_QUESTION } from '../lib/vesselType';

const PRESENCE_TICK_MS = 30_000;

export function BoatSettings() {
  const { boatId, boat, alerts, lastSampleAtMs, refreshBoats } = useBoatChrome();
  const navigate = useNavigate();
  const auth = useAuth();
  // A Try it guest (TOM-868) sets the photo and the screen look. Everything
  // else here relays to the boat or changes its owners, which the server
  // refuses. The owners list is not read for a guest: the server refuses that too.
  const guest = useDemoGuest();
  const owners = useBoatOwners(guest ? null : boatId);
  const boatName = boat?.display_name ?? 'this boat';
  // Other owners turn Delete into Leave and show the helm units panel. Until
  // the list loads, neither panel is drawn rather than the wrong one.
  const otherOwners =
    owners.state.kind === 'ready' ? owners.state.owners.filter((o) => !o.you).length : null;
  useDocumentTitle(boat ? `${boat.display_name} — Settings` : 'Settings — Neomaris');

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), PRESENCE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // The vessel type is set by RELAYING to the boat, so presence decides whether
  // the control can do anything — the same live/online test the Alarms page's
  // ack buttons use.
  const presence = boat
    ? getBoatPresence(boat.last_contacted_at, boat.created_at, lastSampleAtMs, now)
    : 'offline';
  const online = presence === 'live' || presence === 'online';
  // The MIRROR of the boat's own answer, off the alarm-state read (ADR 0173).
  // Undefined until that read lands — the control renders unselected, which is
  // also what unknown looks like, and both are honest: we do not know yet.
  const vesselType =
    alerts.state.kind === 'ready' ? (alerts.state.response.vesselType ?? null) : null;

  return (
    <section data-testid="boat-settings">
      <div className="page-head">
        <h1>Settings</h1>
        <p className="page-sub">Account-level settings for {boatName}.</p>
      </div>

      {guest ? (
        <p className="muted" data-testid="boat-settings-demo">
          In the demo you can change the boat’s photo and the screen look. The boat’s other settings
          and its owners are not available.
        </p>
      ) : null}

      {guest ? null : (
        <>
          <div className="panel" data-testid="boat-settings-vessel-type">
            <h3>{VESSEL_TYPE_QUESTION}</h3>
            <p className="muted">
              Your boat keeps this answer, and it decides which modes your alarms use — a power boat
              never needs the Sailing mode. Changing it sends the answer to {boatName}; this page
              shows what the boat itself reports back.
            </p>
            <VesselTypeChoice
              boatId={boatId}
              vesselType={vesselType}
              boatOnline={online}
              onSent={alerts.refresh}
              testIdPrefix="boat-settings-vessel-type-choice"
            />
          </div>

          <div className="panel" data-testid="boat-settings-battery-chemistry">
            <h3>Battery chemistry</h3>
            <p className="muted">
              Lithium and lead-acid batteries need different alarm thresholds. {boatName} works out
              each battery’s chemistry when the battery reports it. Set it here when it is wrong or
              unknown. Your boat keeps the setting, and this page shows what the boat reports back.
            </p>
            <BatteryChemistryChoice boatId={boatId} boatOnline={online} />
          </div>

          <BoatOwnersPanel
            boatId={boatId}
            boatName={boatName}
            state={owners.state}
            refresh={owners.refresh}
          />

          {otherOwners !== null && otherOwners > 0 ? (
            <DisplayUnitsPanel boatId={boatId} boatName={boatName} boatOnline={online} />
          ) : null}
        </>
      )}

      <BoatPhotoPanel
        boatId={boatId}
        boatName={boatName}
        photo={boat?.photo}
        onChanged={refreshBoats}
      />

      {guest ? null : <ProtectPanel boatId={boatId} boatName={boatName} />}

      {/* A guest is refused the token routes (TOM-867); the section would only error. */}
      {guest ? null : <OperatorTokenPanel boatId={boatId} boatName={boatName} />}

      <div className="panel" data-testid="boat-settings-screen-look">
        <h3 id="boat-settings-screen-look-heading">Screen look</h3>
        <p className="muted">
          System follows this device’s light or dark setting. Night turns the screen red on black
          for the helm at night and stays on this device. Your account remembers the rest, so a new
          browser opens the way you left it.
        </p>
        <ScreenLookChoice
          labelledBy="boat-settings-screen-look-heading"
          testIdPrefix="boat-settings-screen-look"
        />
      </div>

      {!guest && otherOwners !== null && otherOwners > 0 && auth.user !== null ? (
        <div className="panel" data-testid="boat-settings-leave">
          <h3>Leave this boat</h3>
          <p className="muted">
            Removes {boatName} from your account. Its dashboard, alarms and history stop being
            available to you, and alarm notifications stop. The other owners keep the boat and
            everything on it. Only the last owner can delete a boat.
          </p>
          <LeaveBoatConfirm
            boatId={boatId}
            boatName={boatName}
            userId={auth.user.id}
            otherOwners={otherOwners}
            onLeft={() => navigate('/dashboard')}
          />
        </div>
      ) : null}

      {/* An owners list that failed to load still offers Delete: the server
          refuses it with 409 boat_has_other_owners on a shared boat. */}
      {!guest && (otherOwners === 0 || owners.state.kind === 'error') ? (
        <div className="panel" data-testid="boat-settings-delete">
          <h3>Delete this boat</h3>
          <p className="muted">
            Removes {boatName} from your account. Its dashboard, alarms, and history stop being
            available to you, and alarm notifications stop. If you delete it by mistake, contact
            support — we keep the data.
          </p>
          <DeleteBoatConfirm
            boatId={boatId}
            boatName={boatName}
            // The boat is gone from GET /v1/boats now, so staying on a
            // boat-scoped route would just render a 404 shell. /dashboard is the
            // right landing: it re-fetches and routes to whatever is left (the
            // empty state for a last-boat delete).
            onDeleted={() => navigate('/dashboard')}
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * The units on the boat's own displays, for a boat with more than one owner
 * (TOM-458, ADR 0319). Each owner's units in the app are their own; the helm
 * has one set, and a change of one owner's units no longer overwrites it. Here
 * an owner sends theirs on purpose.
 */
function DisplayUnitsPanel({
  boatId,
  boatName,
  boatOnline,
}: {
  boatId: string;
  boatName: string;
  boatOnline: boolean;
}) {
  const units = useUnits();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const summary = [
    unitLabel('speed', units.speed),
    unitLabel('depth', units.depth),
    unitLabel('temperature', units.temperature),
    unitLabel('pressure', units.pressure),
  ].join(', ');

  async function send() {
    setStatus('sending');
    setError(null);
    try {
      await sendDisplayUnits(boatId);
      setStatus('sent');
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not send your units to the boat. Try again.'));
      setStatus('idle');
    }
  }

  return (
    <div className="panel" data-testid="boat-settings-display-units">
      <h3>Units on the boat&apos;s displays</h3>
      <p className="muted">
        {boatName}&apos;s own screens show one set of units for everyone aboard. Changing your units
        in your profile changes only what you see. To put your units ({summary}) on the boat&apos;s
        displays, send them here.
      </p>
      <button
        type="button"
        className="btn"
        disabled={!boatOnline || status === 'sending'}
        data-testid="boat-display-units-send"
        onClick={() => void send()}
      >
        {status === 'sending' ? 'Sending…' : `Use my units on ${boatName}`}
      </button>
      {!boatOnline ? (
        <p className="muted">The boat is offline. It keeps its own units until it is back.</p>
      ) : null}
      {status === 'sent' ? (
        <p className="profile-confirm" role="status" data-testid="boat-display-units-sent">
          Sent. {boatName}&apos;s displays now use your units.
        </p>
      ) : null}
      {error !== null ? (
        <p className="alarm-form-err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
